import * as React from 'react';

import { encrypt } from '@/common/securityUtils';
import { DataTable } from '@/components/dataTable';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import TableSelect from '@/components/dataTable/TableSelect';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useAttributes } from '@/hooks/useAttributes';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { formatDate } from '@/lib/dateFormatter';
import { exportCsvSingleFilePapa } from '@/lib/exportCsv';
import { formatAmount } from '@/lib/format';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import type { StorePaymentLogsDetailsData } from '@/services/payment/payment.types';
import {
  getClubVerificationDisplay,
  getPaymentMethodDisplay,
  getPlanTypeLabel,
  getReservationTypeDisplay,
  getTransactionStatusDisplay,
} from '../constants/storePaymentLogStatus.constant';
import { getColumns } from './Column';

export function IndividualStoreLogTable({
  data,
  isLoading = false,
  pagination,
  storeName,
}: {
  data: StorePaymentLogsDetailsData[];
  isLoading: boolean;
  pagination: Pagination;
  storeName?: string;
}) {
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const { t, locale } = useDynamicLocale();
  const { data: reservationPlanTypes } = useAttributes('reservationPlanType');
  const columns = React.useMemo(
    () => getColumns(t, locale, reservationPlanTypes ?? []),
    [t, locale, reservationPlanTypes],
  );

  const planTypeOptions = React.useMemo(() => {
    if (!reservationPlanTypes?.length) return [];
    return reservationPlanTypes.map((val) => ({
      value: val.value,
      label: locale === 'en' ? val.labelEng || val.label : val.label,
    }));
  }, [reservationPlanTypes, locale]);

  const isJp = locale === 'ja' || !locale;
  const paymentMethodOptions = React.useMemo(
    () => [
      { label: isJp ? '現地払い' : 'On site payment', value: encrypt('1') },
      { label: isJp ? 'ホスログポイント' : 'Pay with Hosulog points', value: encrypt('3') },
    ],
    [isJp],
  );

  const handleExport = () => {
    const exportColumns: Record<
      string,
      { header: string; value: (row: StorePaymentLogsDetailsData, rowIndex: number) => unknown }
    > = {
      sn: { header: t('common.page.tableColumn.sn'), value: (_, rowIndex) => getSerialNumber(table, rowIndex) },
      customer: { header: t('common.page.tableColumn.customer'), value: (row) => row.customer },
      plan: {
        header: t('common.page.tableColumn.plan'),
        value: (row) => {
          const supportedLocale = locale ?? 'ja';
          const label = getPlanTypeLabel(row.plan, reservationPlanTypes ?? [], supportedLocale);
          return label !== '-' ? label : row.plan || '-';
        },
      },
      noOfPeople: {
        header: t('common.page.tableColumn.numberOfPeople'),
        value: (row) => {
          const count = row.noOfPeople ?? row.NumberOfPeople;
          return count != null ? `${count} 名` : '-';
        },
      },
      NumberOfPeople: {
        header: t('common.page.tableColumn.numberOfPeople'),
        value: (row) => {
          const count = row.noOfPeople ?? row.NumberOfPeople;
          return count != null ? `${count} 名` : '-';
        },
      },
      createdDate: {
        header: t('common.page.tableColumn.storeVisitDateAndTime'),
        value: (row) => {
          const visitDateTime = row.visitDateTime;
          const endDateTime = row.endDateTime;
          if (!visitDateTime) return '-';
          const formattedStart = formatDate(visitDateTime, 'YYYY/MM/DD HH:mm');
          const formattedEnd = endDateTime ? formatDate(endDateTime, 'HH:mm') : '';
          return formattedEnd ? `${formattedStart} ~ ${formattedEnd}` : formattedStart;
        },
      },
      visitDateTime: {
        header: t('common.page.tableColumn.storeVisitDateAndTime'),
        value: (row) => {
          const visitDateTime = row.visitDateTime;
          const endDateTime = row.endDateTime;
          if (!visitDateTime) return '-';
          const formattedStart = formatDate(visitDateTime, 'YYYY/MM/DD HH:mm');
          const formattedEnd = endDateTime ? formatDate(endDateTime, 'HH:mm') : '';
          return formattedEnd ? `${formattedStart} ~ ${formattedEnd}` : formattedStart;
        },
      },
      paymentMethod: {
        header: t('common.page.tableColumn.paymentMethod'),
        value: (row) => getPaymentMethodDisplay(row.paymentMethod, locale).label,
      },
      clubVerification: {
        header: t('common.page.tableColumn.clubVerification'),
        value: (row) => getClubVerificationDisplay(row.clubVerification, locale).label,
      },
      transactionStatus: {
        header: t('common.page.tableColumn.transactionStatus'),
        value: (row) => getTransactionStatusDisplay(row.transactionStatus, locale).label,
      },
      reservationType: {
        header: t('common.page.tableColumn.reservationType'),
        value: (row) => getReservationTypeDisplay(row.reservationType, locale),
      },
      planAmount: {
        header: t('common.page.tableColumn.planAmount'),
        value: (row) => formatAmount(row.planAmount),
      },
      totalPlanAmount: {
        header: t('common.page.tableColumn.storeTotalPlanAmount'),
        value: (row) => formatAmount(row.totalPlanAmount),
      },
      totalClubPlanAmount: {
        header: t('common.page.tableColumn.totalClubPlanAmount'),
        value: (row) => formatAmount(row.totalClubPlanAmount),
      },
      adminPlanCommissionAmount: {
        header: t('common.page.tableColumn.adminPlanCommissionAmount'),
        value: (row) => formatAmount(row.adminPlanCommissionAmount),
      },
      totalAdminPlanCommissionAmount: {
        header: t('common.page.tableColumn.totalAdminPlanCommissionAmount'),
        value: (row) => formatAmount(row.totalAdminPlanCommissionAmount),
      },
      adminCommissionAmount: {
        header: t('common.page.tableColumn.adminCommissionAmount'),
        value: (row) => formatAmount(row.adminCommissionAmount),
      },
      totalAdminCommissionAmount: {
        header: t('common.page.tableColumn.storeTotalAdminCommissionAmount'),
        value: (row) => formatAmount(row.totalAdminCommissionAmount),
      },
      totalAdminPayments: {
        header: t('common.page.tableColumn.storeTotalAdminPayments'),
        value: (row) => formatAmount(row.totalAdminPayments),
      },
    };

    const exportColumnsInTableOrder = table
      .getVisibleLeafColumns()
      .flatMap(({ id }) => (exportColumns[id] ? [exportColumns[id]] : []));
    const exportRows = table
      .getFilteredRowModel()
      .rows.map(({ original, index }) =>
        Object.fromEntries(exportColumnsInTableOrder.map(({ header, value }) => [header, value(original, index)])),
      );

    exportCsvSingleFilePapa(exportRows, {
      fileName: 'individual-store-payment-logs',
      headers: exportColumnsInTableOrder.map(({ header }) => header),
    });
  };

  const { table, planFilter, onPlanFilterChange, paymentMethodFilter, onPaymentMethodFilterChange } = useDataTable({
    data,
    columns,
    pageCount,
    initialState: {
      sorting: [{ id: 'createdDate', desc: true }],
    },
    meta: {
      title: (
        <div className="flex gap-1 font-semibold">
          {storeName && <span>{storeName}</span>}
          <Translation id="paymentManagement.storePaymentLogs.paymentLogs.title" />
        </div>
      ),
      totalPage: pagination.totalItems,
      loading: isLoading,
      actionKey: 'exportCsv',
      action: handleExport,
    },
    getRowId: (originalRow) =>
      String(
        originalRow.id ||
          (originalRow.customerId
            ? `${originalRow.customerId}-${originalRow.visitDateTime}`
            : originalRow.visitDateTime),
      ),
    shallow: false,
    clearOnDefault: true,
  });

  const selectedPaymentMethod =
    paymentMethodFilter === 'On Arrival' ? 'On site payment' : (paymentMethodFilter as string) || '';

  return (
    <DataTable table={table}>
      <DataTableToolbar
        planFilter={planFilter as string}
        onPlanFilterChange={onPlanFilterChange}
        paymentMethodFilter={paymentMethodFilter as string}
        onPaymentMethodFilterChange={onPaymentMethodFilterChange}
        filterChildren={
          <div className="flex items-center gap-4">
            <TableSelect
              label={t('common.page.tableColumn.planType')}
              options={planTypeOptions}
              value={(planFilter as string) || ''}
              onValueChange={onPlanFilterChange}
            />
            <TableSelect
              label={t('common.page.tableColumn.paymentMethod')}
              options={paymentMethodOptions}
              value={selectedPaymentMethod}
              onValueChange={onPaymentMethodFilterChange}
            />
          </div>
        }
      />
    </DataTable>
  );
}
